// lib/features/search/search.slice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SearchState } from "./search.state";
import {
  getHomeSearchList,
  saveSearch,
  clearSearchHistory,
  bulkDeleteSearches,
  getSearchResults,
  getFavoriteSearches,
  getSearchSuggestions,
  getImageSearchResults,
} from "./search.thunk";
import {
  HomeSearchListData,
  SearchResultsData,
  FavoriteSearchesData,
  SearchSuggestionsData,
} from "./search.types";
import { searchStorage } from "../../storage/searchStorage";

/** Görsel aramanın `searchResultsQuery` kimliği — metin terimleriyle çakışmaz. */
export const IMAGE_SEARCH_QUERY_KEY = "__image_search__";

const initialState: SearchState = {
  data: null,
  placeholders: [],
  status: "idle",
  error: null,
  saveStatus: "idle",
  clearStatus: "idle",
  deleteStatus: "idle",
  localSearches: [],
  searchResults: null,
  searchResultsStatus: "idle",
  searchResultsError: null,
  searchResultsRequestId: null,
  searchResultsQuery: null,
  favoriteSearches: null,
  favoriteSearchesStatus: "idle",
  favoriteSearchesError: null,
  suggestions: null,
  suggestionsStatus: "idle",
  suggestionsError: null,
  isPhotoSearchOpen: false,
};

export const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    resetSearch: (state) => {
      state.data = null;
      state.placeholders = [];
      state.status = "idle";
      state.error = null;
      state.saveStatus = "idle";
      state.clearStatus = "idle";
      state.deleteStatus = "idle";
    },
    // Arama terimi değişince sonuçları temizler. `searchResultsRequestId` de
    // null'lanır: uçuşta kalan eski terimin isteği döndüğünde artık eşleşmez ve
    // yazılmaz. Not: eski `resetSearch` yalnızca ana sayfa arama listesini
    // (data/placeholders) sıfırlıyordu, searchResults'a hiç dokunmuyordu —
    // "query değişince stale state'i temizle" çağrısı bu yüzden etkisizdi.
    resetSearchResults: (state) => {
      state.searchResults = null;
      state.searchResultsStatus = "idle";
      state.searchResultsError = null;
      state.searchResultsRequestId = null;
      state.searchResultsQuery = null;
    },
    // SSR text-search sonuçlarını Redux'a mount'ta yükler (client fetch flicker'ını
    // önler).
    //
    // Eskiden `status !== "idle"` guard'ı vardı: "yalnızca sonuçlar henüz boşken
    // uygula". Bu guard, sayfa geçişinde sessizce yanlış davranıyordu — arama
    // sayfasından çıkarken slice temizlenmediği için status bir sonraki mount'ta
    // hâlâ "success" oluyor, seed yutuluyor ve ekranda ÖNCEKİ terimin ürünleri
    // kalıyordu (SearchPage aynı anda SSR verisi var diye ilk client fetch'i de
    // atlıyor → hiçbir şey düzeltmiyor, ancak refresh çözüyordu).
    //
    // Seed mount başına bir kez (ssrSeededRef) ve fetch effect'inden ÖNCE
    // çalıştığı için koşulsuz yazmak güvenli. requestId null'lanır: önceki
    // ziyaretten uçuşta kalan bir istek dönerse artık eşleşmez ve yazılmaz.
    loadInitialSearchResults: (
      state,
      action: PayloadAction<{ data: SearchResultsData; query: string }>,
    ) => {
      state.searchResults = action.payload.data;
      state.searchResultsStatus = "success";
      state.searchResultsError = null;
      state.searchResultsRequestId = null;
      state.searchResultsQuery = action.payload.query;
    },
    clearSuggestions: (state) => {
      state.suggestions = null;
      state.suggestionsStatus = "idle";
      state.suggestionsError = null;
    },
    removeSearchItem: (state, action: PayloadAction<number>) => {
      if (state.data) {
        const pastSearchSection = state.data.find((s) => s.type === "list");
        if (pastSearchSection && pastSearchSection.type === "list") {
          pastSearchSection.items = pastSearchSection.items.filter(
            (item) => item.id !== action.payload,
          );
        }
      }
    },
    removeFavoriteSearchItem: (state, action: PayloadAction<number>) => {
      if (state.favoriteSearches) {
        state.favoriteSearches.items = state.favoriteSearches.items.filter(
          (item) => item.id !== action.payload,
        );
      }
    },
    loadLocalSearches: (state) => {
      state.localSearches = searchStorage.getSearches();
    },
    addLocalSearch: (state, action: PayloadAction<string>) => {
      searchStorage.addSearch(action.payload);
      state.localSearches = searchStorage.getSearches();
    },
    removeLocalSearch: (state, action: PayloadAction<number>) => {
      searchStorage.removeSearch(action.payload);
      state.localSearches = searchStorage.getSearches();
    },
    clearLocalSearches: (state) => {
      searchStorage.clearSearches();
      state.localSearches = [];
    },
    openPhotoSearch: (state) => {
      state.isPhotoSearchOpen = true;
    },
    closePhotoSearch: (state) => {
      state.isPhotoSearchOpen = false;
    },
  },
  extraReducers: (build) => {
    build
      .addCase(getHomeSearchList.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        getHomeSearchList.fulfilled,
        (state, action: PayloadAction<HomeSearchListData>) => {
          state.status = "success";
          state.data = action.payload.items;
          state.placeholders = action.payload.placeholders;
        },
      )
      .addCase(
        getHomeSearchList.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.status = "error";
          state.error = action.payload ?? "Bir hata oluştu.";
        },
      )

      .addCase(saveSearch.pending, (state) => {
        state.saveStatus = "loading";
      })
      .addCase(saveSearch.fulfilled, (state) => {
        state.saveStatus = "success";
      })
      .addCase(
        saveSearch.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.saveStatus = "error";
          state.error = action.payload ?? "Arama kaydedilemedi.";
        },
      )

      .addCase(clearSearchHistory.pending, (state) => {
        state.clearStatus = "loading";
      })
      .addCase(clearSearchHistory.fulfilled, (state) => {
        state.clearStatus = "success";
        if (state.data) {
          const pastSearchSection = state.data.find((s) => s.type === "list");
          if (pastSearchSection && pastSearchSection.type === "list") {
            pastSearchSection.items = [];
          }
        }
      })
      .addCase(
        clearSearchHistory.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.clearStatus = "error";
          state.error = action.payload ?? "Geçmiş temizlenemedi.";
        },
      )

      .addCase(bulkDeleteSearches.pending, (state) => {
        state.deleteStatus = "loading";
      })
      .addCase(bulkDeleteSearches.fulfilled, (state) => {
        state.deleteStatus = "success";
      })
      .addCase(
        bulkDeleteSearches.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.deleteStatus = "error";
          state.error = action.payload ?? "Arama silinemedi.";
        },
      )

      .addCase(getSearchResults.pending, (state, action) => {
        const query = action.meta.arg?.search ?? "";
        // Terim değiştiyse sayfa numarasına bakmadan temizle — aksi halde
        // page>1 isteği eski terimin listesine append ederdi.
        const isFirstPage = !action.meta.arg || action.meta.arg.page === 1;
        if (isFirstPage || state.searchResultsQuery !== query) {
          state.searchResults = null;
        }
        state.searchResultsQuery = query;
        state.searchResultsStatus = "loading";
        state.searchResultsError = null;
        state.searchResultsRequestId = action.meta.requestId;
      })
      .addCase(getSearchResults.fulfilled, (state, action) => {
        // Sıradan çıkmış yanıt — bu istek başlatıldıktan sonra yenisi
        // başlatılmış (veya sonuçlar temizlenmiş). Yazılırsa yeni aramanın
        // listesi eski terimin sonuçlarıyla değişir/karışır.
        if (action.meta.requestId !== state.searchResultsRequestId) return;
        state.searchResultsStatus = "success";
        if (action.payload.pagination.current_page === 1) {
          state.searchResults = action.payload;
        } else {
          if (state.searchResults) {
            state.searchResults = {
              ...action.payload,
              products: [
                ...state.searchResults.products,
                ...action.payload.products,
              ],
            };
          } else {
            state.searchResults = action.payload;
          }
        }
      })
      .addCase(getSearchResults.rejected, (state, action) => {
        if (action.meta.requestId !== state.searchResultsRequestId) return;
        state.searchResultsStatus = "error";
        state.searchResultsError =
          action.payload ?? "Arama sonuçları yüklenemedi.";
      })

      .addCase(getImageSearchResults.pending, (state, action) => {
        state.searchResults = null;
        state.searchResultsQuery = IMAGE_SEARCH_QUERY_KEY;
        state.searchResultsStatus = "loading";
        state.searchResultsError = null;
        state.searchResultsRequestId = action.meta.requestId;
      })
      .addCase(getImageSearchResults.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.searchResultsRequestId) return;
        state.searchResultsStatus = "success";
        state.searchResults = action.payload;
      })
      .addCase(getImageSearchResults.rejected, (state, action) => {
        if (action.meta.requestId !== state.searchResultsRequestId) return;
        state.searchResultsStatus = "error";
        state.searchResultsError =
          action.payload ?? "Görsel arama sonuçları yüklenemedi.";
      })

      .addCase(getFavoriteSearches.pending, (state, action) => {
        const isFirstPage = !action.meta.arg || action.meta.arg.page === 1;
        if (isFirstPage) state.favoriteSearches = null;
        state.favoriteSearchesStatus = "loading";
        state.favoriteSearchesError = null;
      })
      .addCase(
        getFavoriteSearches.fulfilled,
        (state, action: PayloadAction<FavoriteSearchesData>) => {
          state.favoriteSearchesStatus = "success";
          if (action.payload.pagination.current_page === 1) {
            state.favoriteSearches = action.payload;
          } else {
            if (state.favoriteSearches) {
              state.favoriteSearches = {
                ...action.payload,
                items: [
                  ...state.favoriteSearches.items,
                  ...action.payload.items,
                ],
              };
            } else {
              state.favoriteSearches = action.payload;
            }
          }
        },
      )
      .addCase(
        getFavoriteSearches.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.favoriteSearchesStatus = "error";
          state.favoriteSearchesError =
            action.payload ?? "Favori aramalar yüklenemedi.";
        },
      )

      // Suggestions
      .addCase(getSearchSuggestions.pending, (state) => {
        state.suggestionsStatus = "loading";
        state.suggestionsError = null;
      })
      .addCase(
        getSearchSuggestions.fulfilled,
        (state, action: PayloadAction<SearchSuggestionsData>) => {
          state.suggestionsStatus = "success";
          state.suggestions = action.payload;
        },
      )
      .addCase(
        getSearchSuggestions.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.suggestionsStatus = "error";
          state.suggestionsError = action.payload ?? "Öneriler yüklenemedi.";
        },
      );
  },
});

export const {
  resetSearch,
  resetSearchResults,
  loadInitialSearchResults,
  clearSuggestions,
  removeSearchItem,
  removeFavoriteSearchItem,
  loadLocalSearches,
  addLocalSearch,
  removeLocalSearch,
  clearLocalSearches,
  openPhotoSearch,
  closePhotoSearch,
} = searchSlice.actions;

export default searchSlice.reducer;
